import type { ReactNode } from "react"
import { Brand } from "./Brand"
import { useAuth } from "@/lib/auth"
import { logout } from "@/lib/api"
import { useBackgroundJobs, type BgJob } from "@/lib/backgroundWork"

/**
 * Shell — header + main + footer. Used by every authed page.
 */
export function Shell({
  children,
  pageEyebrow,
  navItems = [],
  currentPath,
}: {
  children: ReactNode
  pageEyebrow?: string
  navItems?: { href: string; label: string }[]
  currentPath?: string
}) {
  const auth = useAuth()
  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 sm:px-10 pt-7 pb-5 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <a href="/" className="hover:opacity-80 transition-opacity">
            <Brand subtitle={pageEyebrow} />
          </a>
          {navItems.length > 0 && (
            <nav className="hidden sm:flex items-center gap-6">
              {navItems.map((n) => {
                const active = currentPath === n.href
                return (
                  <a
                    key={n.href}
                    href={n.href}
                    className={`font-sans text-sm tracking-tight transition-colors ${
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {n.label}
                    {active && (
                      <span className="block h-px w-full bg-moss mt-1" aria-hidden />
                    )}
                  </a>
                )
              })}
            </nav>
          )}
        </div>
        {auth.status === "authed" && (
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-muted-foreground hidden sm:inline">
              {auth.me.user.email}
            </span>
            <button
              type="button"
              onClick={async () => {
                await logout()
                window.location.href = "/login"
              }}
              className="eyebrow hover:text-foreground transition-colors"
            >
              sign out
            </button>
          </div>
        )}
      </header>
      <div className="rule" />
      {/*
        Pad bottom on mobile so the fixed bottom-tab bar (h~58px + safe-area)
        doesn't cover the last bit of page content.
      */}
      <main className="flex-1 pb-[calc(72px+env(safe-area-inset-bottom))] sm:pb-0">
        {children}
      </main>
      <footer className="hidden sm:flex px-6 sm:px-10 py-6 items-center justify-between text-xs text-muted-foreground">
        <span className="eyebrow">self-hosted · oci · postgres 16</span>
        <span className="font-mono">v0.1.0-α</span>
      </footer>

      {/* Mobile bottom tab bar — primary navigation on phone. */}
      {navItems.length > 0 && auth.status === "authed" && (
        <MobileTabBar items={navItems} currentPath={currentPath} />
      )}

      {/* Background-work pill — surfaces any in-flight or recently-finished
          job on every authed page. Stays out of the way (bottom-right on
          desktop; floats just above the mobile tab bar on phone). Hidden
          when there's nothing to surface. */}
      {auth.status === "authed" && <BgWorkPill currentPath={currentPath} />}
    </div>
  )
}

/**
 * BgWorkPill — persistent indicator for tracked background jobs.
 *
 * Renders up to MAX_VISIBLE pills stacked, newest at top. Jobs whose
 * originPath matches the current page are suppressed (the inline
 * composer/review panel covers them). If there are still more than
 * MAX_VISIBLE non-current-origin jobs after suppression, a small
 * "+N more in progress" trailer surfaces the count.
 */
const MAX_VISIBLE_PILLS = 3

function BgWorkPill({ currentPath }: { currentPath?: string }) {
  const { jobs, dismissJob } = useBackgroundJobs()

  // Suppress pills for the page the user is already on — the inline
  // composer there is the right surface for that job. Order: newest
  // first, already sorted by the provider.
  const visible = jobs.filter((j) => j.originPath !== currentPath)
  if (visible.length === 0) return null

  const shown = visible.slice(0, MAX_VISIBLE_PILLS)
  const overflow = visible.length - shown.length

  return (
    <div
      className="fixed z-50 right-4 sm:right-6 bottom-[calc(72px+env(safe-area-inset-bottom))] sm:bottom-6 max-w-[calc(100vw-2rem)] flex flex-col gap-2 items-end"
      role="status"
      aria-live="polite"
      aria-label={`${visible.length} background job${visible.length === 1 ? "" : "s"}`}
    >
      {shown.map((j) => (
        <PillContent
          key={j.id}
          job={j}
          onDismiss={() => dismissJob(j.id)}
        />
      ))}
      {overflow > 0 && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground pr-1">
          + {overflow} more in progress
        </p>
      )}
    </div>
  )
}

/**
 * PillContent — body link + sibling dismiss button.
 *
 * The dismiss × MUST be a sibling of the <a>, not a descendant. Earlier
 * version put × inside the <a> and used e.preventDefault() in onClick to
 * suppress navigation, but the document-level click interceptor in
 * App.tsx (which intercepts every internal-link click for SPA routing)
 * doesn't check `e.defaultPrevented` before calling history.pushState —
 * so the × would dismiss AND navigate. Sibling layout side-steps the
 * issue entirely: the × click never bubbles through an <a>.
 *
 * `key={top.id}` on the parent ensures a fresh mount when one pill is
 * dismissed and a new job slides into the top slot — the fade-in
 * animation gets to play again.
 */
function PillContent({
  job,
  onDismiss,
}: {
  job: BgJob
  onDismiss: () => void
}) {
  const inFlight = job.status === "pending" || job.status === "running"
  const succeeded = job.status === "succeeded"
  const failed = job.status === "failed" || job.status === "expired"

  // Accent dot — moss for in-flight (pulses), persimmon for failure,
  // moss-solid for success. Matches the editorial vocabulary used in
  // the stage indicator on /today.
  const dotTone = failed
    ? "bg-persimmon"
    : succeeded
    ? "bg-moss"
    : "bg-foreground animate-pulse"

  const headline = inFlight
    ? "parsing your dish…"
    : succeeded
    ? "parse ready"
    : "parse failed"

  return (
    <div className="flex items-center gap-1 bg-surface border border-border rounded-md shadow-sm pl-4 pr-2 py-1 animate-fade-up">
      <a
        href={job.originPath}
        className="flex items-center gap-3 py-2 pr-2 hover:opacity-90 transition-opacity flex-1 min-w-0"
      >
        <span aria-hidden className={`block w-2 h-2 rounded-full ${dotTone}`} />
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {headline}
          </p>
          <p className="font-sans text-sm text-foreground truncate max-w-[16rem]">
            {job.label}
          </p>
        </div>
      </a>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="self-stretch px-2 font-mono text-xs text-muted-foreground hover:text-persimmon transition-colors"
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  )
}

/**
 * MobileTabBar — fixed bottom navigation visible only below the `sm`
 * breakpoint. The desktop inline nav lives in the header; on mobile the
 * header just shows brand + sign-out, and this bar carries primary
 * navigation. iOS safe-area-inset padding keeps labels above the home
 * indicator.
 *
 * Editorial aesthetic kept: mono uppercase labels, hairline borders, moss
 * underline on active. No icons — labels-only is brutal/minimal and matches
 * the rest of the magazine.
 */
function MobileTabBar({
  currentPath,
}: {
  items: { href: string; label: string }[]
  currentPath?: string
}) {
  // Cap at 5. Hardcoded here so the tab bar is consistent across pages
  // regardless of what each page passes via navItems. (Previously this
  // filtered the page's navItems by a PRIORITY list, which silently
  // dropped a tab if a page forgot to include that route — that's how
  // /recipes vanished from the bar after we renamed the route.)
  //
  // Five slots; Performance earned a spot at the expense of Weight (the
  // weight surface is one tap from Dashboard via its quick link, and
  // Performance is a daily-driver check-in like Today).
  const visible: { href: string; label: string }[] = [
    { href: "/today", label: "Today" },
    { href: "/", label: "Dashboard" },
    { href: "/performance", label: "Performance" },
    { href: "/recipes", label: "Recipes" },
    { href: "/profile", label: "Profile" },
  ]

  return (
    <nav
      aria-label="Primary navigation"
      className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-surface border-t border-border pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5">
        {visible.map((n) => {
          const active = currentPath === n.href
          return (
            <li key={n.href}>
              <a
                href={n.href}
                aria-current={active ? "page" : undefined}
                className={`flex flex-col items-center justify-center gap-1 h-[58px] font-mono text-[10px] uppercase tracking-wider transition-colors ${
                  active
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span>{shortLabel(n.label)}</span>
                {active ? (
                  <span
                    aria-hidden
                    className="block h-px w-6 bg-moss"
                  />
                ) : (
                  <span aria-hidden className="block h-px w-6 bg-transparent" />
                )}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

// Trim multi-word labels (e.g. "Recipes" stays, "Dashboard" → "Home") so the
// five-column grid stays readable on narrow phones (320-360px wide).
function shortLabel(label: string): string {
  const lower = label.toLowerCase()
  if (lower === "dashboard") return "Home"
  if (lower === "performance") return "Perf"
  return label
}
